import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChannelKind } from './types';
import { useCommunityForum } from './CommunityForumContext';
import {
  fetchCommunityChannelGrants,
  fetchCommunityMemberFilterOptions,
  fetchCommunityMembersForAccess,
  type CommunityMemberForAccess,
  type CommunityQuestionnaireFilter,
} from '../../services/communityService';
import { grantToLocal } from '../../services/communityApiMappers';

type Props = {
  open: boolean;
  onClose: () => void;
  channelKind: ChannelKind;
  channelId: string;
  channelName: string;
};

const QUESTIONNAIRE_OPTIONS: { value: CommunityQuestionnaireFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'yes', label: 'Заполнено' },
  { value: 'no', label: 'Не заполнено' },
  { value: 'pending', label: 'На проверке' },
];

function questionnaireLabel(status: string): string {
  if (status === 'complete') return 'Заполнено';
  if (status === 'pending_review') return 'На проверке';
  return 'Не заполнено';
}

export default function CommunityChannelAccessModal({
  open,
  onClose,
  channelKind,
  channelId,
  channelName,
}: Props) {
  const { grantChannelAccessToUser, revokeChannelAccessForUser } = useCommunityForum();

  const [search, setSearch] = useState('');
  const [sector, setSector] = useState('');
  const [company, setCompany] = useState('');
  const [positionFilter, setPositionFilter] = useState<CommunityQuestionnaireFilter>('all');
  const [gradingFilter, setGradingFilter] = useState<CommunityQuestionnaireFilter>('all');

  const [sectors, setSectors] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [members, setMembers] = useState<CommunityMemberForAccess[]>([]);
  const [grants, setGrants] = useState<ReturnType<typeof grantToLocal>[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadGrants = useCallback(async () => {
    const res = await fetchCommunityChannelGrants(channelId);
    if (!res.ok) {
      setGrants([]);
      return;
    }
    setGrants(res.data.map((g) => grantToLocal(channelKind, channelId, g)));
  }, [channelId, channelKind]);

  useEffect(() => {
    if (!open) return;
    void loadGrants();
  }, [open, loadGrants]);

  const grantedEmails = useMemo(() => new Set(grants.map((g) => g.userEmail)), [grants]);

  const loadFilterOptions = useCallback(async () => {
    const res = await fetchCommunityMemberFilterOptions();
    if (!res.ok) return;
    setSectors(res.data.sectors);
    setCompanies(res.data.companies);
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await fetchCommunityMembersForAccess({
      search,
      sector: sector || undefined,
      company: company || undefined,
      position: positionFilter,
      grading: gradingFilter,
    });
    setLoading(false);
    if (!res.ok) {
      setMembers([]);
      setLoadError(res.error);
      return;
    }
    setMembers(res.data);
  }, [search, sector, company, positionFilter, gradingFilter]);

  useEffect(() => {
    if (!open) return;
    void loadFilterOptions();
  }, [open, loadFilterOptions]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void loadMembers();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [open, loadMembers]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSector('');
      setCompany('');
      setPositionFilter('all');
      setGradingFilter('all');
      setMembers([]);
      setLoadError(null);
    }
  }, [open]);

  const toggleAccess = (member: CommunityMemberForAccess, hasAccess: boolean) => {
    if (hasAccess) {
      void revokeChannelAccessForUser(channelKind, channelId, member.email).then(() => loadGrants());
      return;
    }
    void grantChannelAccessToUser(channelKind, channelId, member.email, member.label, member.userId).then(() => loadGrants());
  };

  const grantedInList = useMemo(
    () => members.filter((m) => grantedEmails.has(m.email.toLowerCase())).length,
    [members, grantedEmails],
  );

  if (!open) return null;

  const kindLabel = channelKind === 'chat' ? 'чату' : 'форуму';

  return (
    <div className="dcf-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="dcf-modal dcf-modal--wide dcf-modal--access"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <h2 className="dcf-modal__title">Доступ к {kindLabel}</h2>
        <p className="dcf-modal__hint">
          «{channelName}» — по приглашению. Отметьте участников, которым будет виден раздел.
        </p>

        <div className="dcf-channel-access-filters">
          <input
            className="dcf-input"
            placeholder="Поиск по имени, компании, сектору"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="dcf-select" value={sector} onChange={(e) => setSector(e.target.value)}>
            <option value="">Все секторы</option>
            {sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className="dcf-select" value={company} onChange={(e) => setCompany(e.target.value)}>
            <option value="">Все компании</option>
            {companies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            className="dcf-select"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value as CommunityQuestionnaireFilter)}
          >
            {QUESTIONNAIRE_OPTIONS.map((o) => (
              <option key={`pos-${o.value}`} value={o.value}>
                Должность: {o.label}
              </option>
            ))}
          </select>
          <select
            className="dcf-select"
            value={gradingFilter}
            onChange={(e) => setGradingFilter(e.target.value as CommunityQuestionnaireFilter)}
          >
            {QUESTIONNAIRE_OPTIONS.map((o) => (
              <option key={`gr-${o.value}`} value={o.value}>
                Грейдинг: {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="dcf-channel-access-summary">
          <span>
            В списке: <strong>{members.length}</strong>
          </span>
          <span>
            С доступом (всего): <strong>{grants.length}</strong>
          </span>
          <span>
            С доступом (в фильтре): <strong>{grantedInList}</strong>
          </span>
        </div>

        {loadError && (
          <p className="text-sm text-amber-700 mt-3">{loadError}</p>
        )}

        <div className="dcf-channel-access-table-wrap">
          {loading ? (
            <p className="text-sm text-slate-500 p-4">Загрузка участников…</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-500 p-4">Участники не найдены. Измените фильтры.</p>
          ) : (
            <table className="dcf-admin-table dcf-channel-access-table">
              <thead>
                <tr>
                  <th>Участник</th>
                  <th>Компания</th>
                  <th>Сектор</th>
                  <th>Должность</th>
                  <th>Грейдинг</th>
                  <th className="dcf-channel-access-table__access-col">Доступ</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const hasAccess = grantedEmails.has(member.email.toLowerCase());
                  return (
                    <tr key={member.userId} className={hasAccess ? 'dcf-channel-access-table__row--granted' : ''}>
                      <td>
                        <div className="font-medium text-slate-800">{member.label}</div>
                        {member.title && (
                          <div className="text-xs text-slate-500">{member.title}</div>
                        )}
                      </td>
                      <td>{member.company || '—'}</td>
                      <td>{member.sector || '—'}</td>
                      <td>
                        <span className="dcf-channel-access-badge">
                          {questionnaireLabel(member.positionStatus)}
                        </span>
                      </td>
                      <td>
                        <span className="dcf-channel-access-badge">
                          {questionnaireLabel(member.gradingStatus)}
                        </span>
                      </td>
                      <td className="dcf-channel-access-table__access-col">
                        <label className="dcf-channel-access-toggle">
                          <input
                            type="checkbox"
                            checked={hasAccess}
                            onChange={() => toggleAccess(member, hasAccess)}
                          />
                          <span>{hasAccess ? 'Есть' : 'Нет'}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dcf-modal__actions">
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={onClose}>
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
